'use client';

import React from 'react';
import * as HeroIcons from '@heroicons/react/24/outline';
import * as HeroIconsSolid from '@heroicons/react/24/solid';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { CID_ICONS, isCidIconName } from '@/components/ui/cidIcons';
import type { CidIconDef } from '@/components/ui/cidIcons';

type IconVariant = 'outline' | 'solid';
type HeroIconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

interface IconProps extends Omit<React.SVGProps<SVGSVGElement>, 'name' | 'ref'> {
  /** Heroicon component name (e.g. "Bars3Icon") or a CanIDeal icon key (e.g. "leaf"). */
  name: string;
  variant?: IconVariant;
  size?: number;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
}

/** Single icon component: Heroicons for legacy pages + the approved canideal.org set. */
export function AppIcon({
  name,
  variant = 'outline',
  size = 24,
  className = '',
  onClick,
  disabled = false,
  ...props
}: IconProps) {
  const interaction = disabled
    ? 'opacity-50 cursor-not-allowed'
    : onClick
      ? 'cursor-pointer hover:opacity-80'
      : '';
  const handleClick = disabled ? undefined : onClick;

  if (isCidIconName(name)) {
    const icon: CidIconDef = CID_ICONS[name];
    const round = icon.round !== false;
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox={icon.viewBox ?? '0 0 24 24'}
        fill="none"
        stroke="currentColor"
        strokeWidth={icon.strokeWidth}
        strokeLinecap={round ? 'round' : undefined}
        strokeLinejoin={round ? 'round' : undefined}
        aria-hidden="true"
        className={`${interaction} ${className}`.trim() || undefined}
        onClick={handleClick}
        {...props}
      >
        {icon.nodes.map(([Tag, attrs], index) => (
          <Tag key={index} {...attrs} />
        ))}
      </svg>
    );
  }

  const iconSet = (variant === 'solid' ? HeroIconsSolid : HeroIcons) as unknown as Record<
    string,
    HeroIconComponent
  >;
  const IconComponent = iconSet[name];

  if (!IconComponent) {
    return (
      <QuestionMarkCircleIcon
        width={size}
        height={size}
        className={`text-muted-foreground ${interaction} ${className}`}
        onClick={handleClick}
        {...props}
      />
    );
  }

  return (
    <IconComponent
      width={size}
      height={size}
      className={`${interaction} ${className}`}
      onClick={handleClick}
      {...props}
    />
  );
}

/** Legacy default export — existing pages import `Icon` by default. */
export default AppIcon;
